import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Styles from './vm-card.scss';
import { classMap } from 'lit/directives/class-map.js';
import 'Components/link-overlay/vm-link-overlay';

/**
 * A card component with composable slots for avatar, text, CTA, and description.
 *
 * When `href` is set, the body (avatar + text + CTA) is wrapped in `vm-link-overlay` so the
 * whole header becomes one primary link — vm-card renders the overlay anchor itself, so
 * consumers don't author it. The `description` stays outside the overlay, so links inside it
 * remain independently clickable without any z-index juggling.
 *
 * @attr href       - Optional. When set, the card header links here.
 * @attr link-label - Optional. Accessible name for the header link (the overlay anchor has
 *                    no visible text, so set this when `href` is used).
 * @attr target     - Optional. Anchor `target` for the header link.
 * @slot avatar - Optional. Leading avatar or image.
 * @slot text - Required. Primary text content.
 * @slot cta - Optional. Call-to-action element (e.g. a button).
 * @slot description - Optional. Secondary descriptive text below the main content.
 */
@customElement('vm-card')
export class VmCard extends LitElement {
    static override styles = Styles;

    @property({ type: String }) href = '';
    @property({ type: String, attribute: 'link-label' }) linkLabel = '';
    @property({ type: String }) target: HTMLAnchorElement['target'] = '';

    override render() {
        const hasAvatar = !!this.querySelector(':scope > [slot="avatar"]');
        const hasCta = !!this.querySelector(':scope > [slot="cta"]');
        const hasDescription = !!this.querySelector(':scope > [slot="description"]');

        const body = html`
            <div class="vm-card__body">
                ${hasAvatar ? html`<div class="vm-card__avatar"><slot name="avatar"></slot></div>` : nothing}
                <div class="vm-card__text">
                    <slot name="text"></slot>
                </div>
                ${hasCta ? html`<div class="vm-card__cta"><slot name="cta"></slot></div>` : nothing}
            </div>
        `;

        // When `href` is set, wrap only the body in vm-link-overlay so the header becomes one
        // primary link. vm-link-overlay's own styles stretch this `overlay-link` anchor to
        // cover the body; the description sits outside the overlay and stays clickable.
        const header = this.href
            ? html`
                  <vm-link-overlay>
                      ${body}
                      <a
                          slot="link-overlay"
                          href=${this.href}
                          aria-label=${this.linkLabel || nothing}
                          target=${this.target || nothing}
                      ></a>
                  </vm-link-overlay>
              `
            : body;

        return html`
            <div class=${classMap({ 'vm-card__inner': true, 'vm-card__inner--has-description': hasDescription })}>
                ${header}
                ${hasDescription
                    ? html`<div class="vm-card__description"><slot name="description"></slot></div>`
                    : nothing}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-card': VmCard;
    }
}
